import { useEffect, useState } from "react";
import AppsSideBarFolderItem from "./SideBarFolderItem";
import AppList from "@lib/components/AppComponents/AppList";
import ListEmptyResult from "@lib/components/AppComponents/AppList/ListEmptyResult";
import SidebarPlaceholder from "@lib/components/AppComponents/AppSkeleton/SidebarListSkeleton";
import {
  StyledSidebarContent,
  StyledSidebarFooter,
  StyledSidebarHeader,
  StyledSidebarList,
  StyledSidebarScrollbar,
} from "./index.styled";
import { PaginationType, UseQueryProps } from "@lib/types/Api";
import { Wallet } from "@lib/types/Billing";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { useNavigate, useParams } from "react-router-dom";
import { Tag, Typography } from "antd";
import { red } from "@ant-design/colors";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { useWalletQueries } from "@lib/apiQueries";
import { useSidebar } from "@lib/context/SidebarContext";
import { AppContext } from "@lib/types";
import { getWalletLinkNoFilter } from "@app/shared/walletAddressListBilling/Sidebar/utils";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { BillingWalletModal } from "@lib/components/PageComponents/Wallet/WalletModal";

type Props = {
  walletsQuery: UseQueryProps<PaginationType<Wallet>>;
  context: AppContext;
  customerId?: string;
  leadId?: string;
};

const Sidebar: React.FC<Props> = ({
  walletsQuery,
  context,
  customerId,
  leadId,
}) => {
  const { messages } = useIntl();
  const { walletId } = useParams();
  const navigate = useNavigate();
  const [openModal, setModalOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<Wallet>();
  const { collapsed } = useSidebar();

  useEffect(() => {
    if (!openModal) setSelectedWallet(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };
  const handleSelectWallet = (wallet: Wallet) => {
    setSelectedWallet(wallet);
    toggle(true);
  };

  const { addWalletMutation, updateWalletMutation, deleteWalletMutation } =
    useWalletQueries({
      messages,
    });
  const { data, isLoading, isFetching, refetch } = walletsQuery;
  const apiData = data?.data.data;

  useEffect(() => {
    if (
      addWalletMutation.isSuccess ||
      updateWalletMutation.isSuccess ||
      deleteWalletMutation.isSuccess
    ) {
      toggle(false);
    }
  }, [
    addWalletMutation.isSuccess,
    updateWalletMutation.isSuccess,
    deleteWalletMutation.isSuccess,
  ]);

  return (
    <>
      <StyledSidebarHeader>
        <BillingWalletModal
          mutationQuery={
            selectedWallet ? updateWalletMutation : addWalletMutation
          }
          walletQuery={walletsQuery}
          openModal={openModal}
          toggleModal={toggle}
          wallet={selectedWallet}
        />

        <StyledFlex
          style={{
            flexDirection: collapsed ? "column" : "row",
            justifyContent: "space-between",
          }}
        >
          <div>
            {walletId ? (
              <Tag
                closable
                color={red[4]}
                onClose={() => {
                  navigate(getWalletLinkNoFilter(context));
                }}
              >
                {collapsed
                  ? (messages["common.filter"] as string)
                  : (messages["common.removeFilter"] as string)}
              </Tag>
            ) : (
              <>
                {!collapsed && (
                  <Typography.Text>
                    {messages["common.wallets"] as string}
                  </Typography.Text>
                )}
              </>
            )}
          </div>

          <StyledFlex style={{ justifyContent: "flex-end", marginRight: 0 }}>
            <ActionButton
              type="add"
              noLabel
              noBg
              onClick={() => toggle(true)}
            />

            <ActionButton
              type="refresh"
              noLabel
              onClick={() => refetch()}
              disabled={isFetching}
              loading={isFetching}
            />
          </StyledFlex>
        </StyledFlex>
      </StyledSidebarHeader>

      <StyledSidebarScrollbar>
        <StyledSidebarContent>
          <StyledSidebarList
            aria-label="wallet list"
            loading={isLoading || isFetching}
          >
            <AppList
              data={apiData}
              ListEmptyComponent={
                <ListEmptyResult
                  loading={isLoading || isFetching}
                  placeholder={<SidebarPlaceholder />}
                />
              }
              renderItem={(item) => (
                <AppsSideBarFolderItem
                  key={item.id}
                  item={item}
                  deleteQuery={deleteWalletMutation}
                  handleSelectWallet={handleSelectWallet}
                  sidebarCollapsed={collapsed}
                  context={context}
                  customerId={customerId}
                  leadId={leadId}
                  walletQuery={walletsQuery}
                />
              )}
            />
          </StyledSidebarList>
        </StyledSidebarContent>
      </StyledSidebarScrollbar>

      <StyledSidebarFooter
        message={
          !collapsed ? (
            <FormattedMessage
              id={"common.totalWalletCount"}
              values={{ total: data?.data?.total }}
            />
          ) : (
            <>
              <FormattedMessage id={"common.total"} /> {data?.data?.total}
            </>
          )
        }
      />
    </>
  );
};

export default Sidebar;
